import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { IApiSmpGptAgent } from "@smp/api";
import { useSocialPageGroupSetQuery } from "@smp/graphql";
import {
  IUiSelectProps,
  UiBox,
  UiButton,
  UiCheckboxV2,
  UiChip,
  UiFormControlLabel,
  UiHelper,
  UiSelect,
  UiTextFieldV2,
  UiTypography,
} from "@smp/ui";
import { dataArrayAddOrRemove, dataArrayToHashMap } from "@smp/utils";
import { IconChevronLeft, IconWand } from "@tabler/icons-react";
import clsx from "clsx";
import { useFormik } from "formik";
import { FC, useMemo } from "react";
import * as Yup from "yup";

import { UiGptLanguageSelect } from "@/components/ui/gpt_language_select";
import {
  GPT_POST_EXTRA_FIELDS,
  GPT_TEXT_LENGHT_PRESETS,
  GPT_TEXT_STYLES,
} from "@/fixtures";
import { gqlClient } from "@/hooks/use_graphql";
import { useMutationGptAgentPatch } from "@/queries";
import { dialogAdvisorConfig } from "@/screens/app/components/dialogs/dialog_advisor";
import { Disclaimer } from "@/screens/app/components/dialogs/dialog_advisor/components/disclaimer";
import { FormNumberTextField } from "@/screens/app/components/dialogs/dialog_advisor/components/form_number_text_field";
import { INITIAL_AGENT_PARAMS_POST } from "@/screens/app/components/dialogs/dialog_advisor/fixtures/agent_params_post";
import { generateAgentContext } from "@/screens/app/components/dialogs/dialog_advisor/helpers/generate_agent_context";
import { metrikaCombinedGoal } from "@/utils";

import classes from "./agent_post_settings.module.css";

export interface IAgentPostSettingsProps {
  agent: IApiSmpGptAgent;
  agentIdx: number;
  onCancel: () => void;
  onGenerate: (agent: IApiSmpGptAgent) => void;
}

export const AgentPostSettings: FC<IAgentPostSettingsProps> = ({
  agent,
  agentIdx,
  onCancel,
  onGenerate,
}) => {
  const { i18n } = useLingui();

  const { mutateAsync: patchAgent, isLoading: isPatching } =
    useMutationGptAgentPatch({
      onSuccess: () => {
        metrikaCombinedGoal("gpt_agent_update", { advisor: 1 });
      },
    });

  const {
    values,
    setFieldValue,
    handleSubmit,
    dirty,
    errors,
    getFieldMeta,
    setFieldTouched,
  } = useFormik<NonNullable<IApiSmpGptAgent["params"]>>({
    initialValues: {
      socialPageGroupId: agent.params?.socialPageGroupId,
      context: agent.params?.context,
      post: {
        ...INITIAL_AGENT_PARAMS_POST,
        language: i18n.locale,
        ...agent.params?.post,
      },
    },
    validationSchema: Yup.object().shape({
      post: Yup.object().shape({
        topic: Yup.string()
          .trim()
          .max(
            dialogAdvisorConfig.ask_input_max_length,
            t`Max. length is ${dialogAdvisorConfig.ask_input_max_length} characters`,
          ),
      }),
    }),
    onSubmit: async (values) => {
      metrikaCombinedGoal("advisor_click_generate_posts", { advisor: 1 });

      let updateAgent = agent;

      if (dirty) {
        updateAgent = await patchAgent({
          id: agent.id,
          context: generateAgentContext(values),
          params: {
            socialPageGroupId: agent.params?.socialPageGroupId,
            ...values,
          },
        });
      }

      onGenerate(updateAgent);
    },
  });

  const topicError = useMemo<string | undefined>(
    () => getFieldMeta("post.topic").touched && (errors.post as any)?.topic,
    [errors.post, getFieldMeta],
  );

  const { data: socialPageGroupData } = useSocialPageGroupSetQuery(gqlClient, {
    all_organization_contexts: false,
  });

  const socialPageGroupOptions = useMemo(
    () =>
      socialPageGroupData?.social_page_group_set.map((group) => ({
        label: group.name,
        value: group.id,
      })) || [],
    [socialPageGroupData?.social_page_group_set],
  );

  const styleOptions = useMemo<IUiSelectProps["options"]>(
    () =>
      GPT_TEXT_STYLES.map((style) => ({
        label: style.getDisplayName(),
        value: style.id,
      })),
    [],
  );

  const styleOptionsNormalized = useMemo(
    () => dataArrayToHashMap(styleOptions, "value"),
    [styleOptions],
  );

  return (
    <>
      <div className={classes.container}>
        <div className={classes.name}>
          <Trans>Preset</Trans> {agentIdx + 1}
        </div>

        <UiTypography variant="h4" className={classes.title}>
          {agent.params?.context?.niche}
        </UiTypography>

        <div className={classes.form}>
          <div className={classes.form__row}>
            <UiTypography variant="h5">
              <Trans>What are we writing about today? (optional)</Trans>
            </UiTypography>

            <div>
              <UiTextFieldV2
                fullWidth
                multiline
                variant="outlined"
                placeholder={t`__advisor.create_agent.form.placeholder.topic`}
                value={values.post?.topic || ""}
                onChange={(e) => setFieldValue("post.topic", e.target.value)}
                error={!!topicError}
                errorMessage={topicError}
                onBlur={() => setFieldTouched("post.topic")}
              />
            </div>
          </div>

          <div className={clsx(classes.form__row, classes.form__row_col)}>
            <div className={classes.form__row__col}>
              <UiTypography variant="h5">
                <Trans>Posts length</Trans>
              </UiTypography>

              <div className={classes.form__radio}>
                {GPT_TEXT_LENGHT_PRESETS.map((preset) => {
                  const isActive = values?.post?.text_length === preset.id;

                  return (
                    <UiButton
                      key={preset.id}
                      className={clsx(
                        classes.radio,
                        isActive && classes.radio_active,
                      )}
                      size="small"
                      onClick={() =>
                        setFieldValue("post.text_length", preset.id)
                      }
                    >
                      {preset.getDisplayName()}
                    </UiButton>
                  );
                })}
              </div>
            </div>

            {socialPageGroupOptions.length > 1 && (
              <div className={classes.form__row__col}>
                <UiTypography variant="body2">
                  <Trans>What project will we work on?</Trans>
                </UiTypography>

                <UiSelect
                  options={socialPageGroupOptions}
                  fullWidth
                  value={values?.socialPageGroupId}
                  onChange={(e) => {
                    setFieldValue("socialPageGroupId", e.target.value);
                  }}
                />
              </div>
            )}
          </div>

          <div className={clsx(classes.form__row, classes.form__row_col)}>
            <div className={classes.form__row__col}>
              <UiTypography variant="h5">
                <Trans>Number of posts</Trans>
              </UiTypography>

              <div className={classes.form__num}>
                <FormNumberTextField
                  value={values?.post?.num || dialogAdvisorConfig.min_posts_num}
                  inputProps={{
                    min: dialogAdvisorConfig.min_posts_num,
                    max: dialogAdvisorConfig.max_posts_num,
                  }}
                  onChange={(e) => setFieldValue("post.num", e.target.value)}
                />
              </div>
            </div>

            <div className={classes.form__row__col}>
              <UiTypography variant="h5">
                <Trans>Language of posts</Trans>
              </UiTypography>

              <UiGptLanguageSelect
                value={values?.post?.language || "ru"}
                onChange={(language) =>
                  setFieldValue("post.language", language)
                }
                emojiSize="large"
              />
            </div>
          </div>

          <div className={clsx(classes.form__row, classes.form__row_col)}>
            <div className={classes.form__row__col}>
              <div className={classes.form__title}>
                <UiTypography variant="h5">
                  <Trans>Add to post</Trans>
                </UiTypography>
                <UiHelper
                  title={t`Ask the AI SMM assistant to add
                   title or call to action to the post`}
                  arrow={false}
                  size="big"
                />
              </div>

              {GPT_POST_EXTRA_FIELDS.map((field) => {
                const checked = !!values?.post?.extra_fields?.includes(
                  field.id,
                );

                return (
                  <UiFormControlLabel
                    key={field.id}
                    control={
                      <UiCheckboxV2
                        checked={checked}
                        onChange={() => {
                          setFieldValue(
                            "post.extra_fields",
                            dataArrayAddOrRemove(
                              values?.post?.extra_fields || [],
                              field.id,
                            ),
                          );
                        }}
                      />
                    }
                    label={field.getDisplayName()}
                  />
                );
              })}
            </div>

            <div className={classes.form__row__col}>
              <UiTypography variant="h5">
                <Trans>Text style</Trans>
              </UiTypography>

              <UiSelect
                options={styleOptions}
                fullWidth
                value={values?.post?.text_style?.at(0) || ""}
                renderValue={(value) => (
                  <UiBox
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      margin: "-5px 0 -6px",
                    }}
                  >
                    <UiChip
                      label={styleOptionsNormalized[value as string].label}
                    />
                  </UiBox>
                )}
                onChange={(e) => {
                  setFieldValue("post.text_style", [e.target.value]);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Disclaimer mt={3} />

      <div className={classes.buttons}>
        <UiButton
          startIcon={<IconChevronLeft />}
          size="large"
          onClick={onCancel}
          disabled={isPatching}
        >
          <Trans>Back</Trans>
        </UiButton>

        <UiButton
          endIcon={<IconWand />}
          variant="contained"
          color="success"
          size="large"
          onClick={handleSubmit}
          isLoading={isPatching}
        >
          <Trans>Generate posts</Trans>
        </UiButton>
      </div>
    </>
  );
};
